// Release B-Fidelity-2b · Slice F frontend — Applicant slot picker.
// Route: /access-request/:token/scheduling (public, token-authenticated)
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#12233F";
const CREAM = "#F5F1E8";
const GOLD = "#C6A15B";

export default function AccessScheduling() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const load = async (m) => {
    setError(null);
    try {
      const r = await axios.get(`${API}/public/access-requests/scheduling/${token}${m ? `?method=${m}` : ""}`);
      setData(r.data);
      if (!method && r.data.methods_offered?.[0]) setMethod(r.data.methods_offered[0]);
    } catch (e) {
      setError(e?.response?.data?.detail?.code || "load_failed");
    }
  };
  useEffect(() => { load(""); }, [token]);
  useEffect(() => { if (method) load(method); }, [method]);

  const groupedByDay = useMemo(() => {
    if (!data) return {};
    const g = {};
    for (const s of data.slots || []) {
      const day = s.starts_at.slice(0, 10);
      (g[day] = g[day] || []).push(s);
    }
    return g;
  }, [data]);

  const book = async () => {
    if (!selected) return;
    setBooking(true);
    try {
      const r = await axios.post(`${API}/public/access-requests/scheduling/${token}/book`, {
        availability_id: selected.availability_id,
        starts_at: selected.starts_at,
        method: selected.method,
        notes_public: notes.trim() || null,
      });
      setConfirmation(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.code || d?.message || "book_failed");
    } finally { setBooking(false); }
  };

  if (error) return (
    <Wrapper><Panel>
      <h1 style={h1Style}>This link cannot be used</h1>
      <p data-testid="sch-error-code">Reason: <code>{error}</code></p>
      <Link to="/" style={linkStyle}>← ERF Enterprises home</Link>
    </Panel></Wrapper>
  );
  if (!data) return <Wrapper><Panel><p>Loading available times…</p></Panel></Wrapper>;

  if (confirmation) return (
    <Wrapper><Panel>
      <h1 data-testid="sch-success-title" style={h1Style}>You're on the schedule</h1>
      <p style={{ marginTop: 8 }}>{confirmation.message}</p>
      <p style={{ marginTop: 8, fontSize: 13, color: "#475569" }}>
        {new Date(confirmation.starts_at).toLocaleString()} · {confirmation.method}
      </p>
      <p style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
        State: <b>{confirmation.state}</b>{confirmation.auto_confirmed ? " · confirmed" : " · pending confirmation"}. You will receive an email confirmation.
      </p>
      <Link to="/" style={linkStyle}>← ERF Enterprises home</Link>
    </Panel></Wrapper>
  );

  return (
    <Wrapper>
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "48px 20px" }}>
        <div style={{ borderTop: `4px solid ${GOLD}`, paddingTop: 24 }}>
          <div style={eyebrowStyle}>ERF ConstructionOS · Portal Access</div>
          <h1 data-testid="sch-title" style={h1Style}>Pick a time to speak with Edwin's team</h1>
          <p style={{ marginTop: 8, color: "#475569", fontSize: 14 }}>
            Choose the meeting method that works for you, then pick an available time.
          </p>
        </div>

        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", color: "#475569", letterSpacing: 1 }}>Meeting method</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {(data.methods_offered || []).map((m) => (
              <button key={m} data-testid={`sch-pick-method-${m}`} onClick={() => { setMethod(m); setSelected(null); }}
                       style={{ padding: "8px 14px", borderRadius: 9999, cursor: "pointer",
                                border: `1px solid ${method === m ? NAVY : "#cbd5e1"}`,
                                background: method === m ? NAVY : "white",
                                color: method === m ? CREAM : "#0f172a", fontSize: 13, fontWeight: 600 }}>
                {m}
              </button>
            ))}
          </div>
        </div>

        <div data-testid="sch-slot-grid" style={{ marginTop: 24, display: "grid", gap: 16 }}>
          {Object.keys(groupedByDay).length === 0 && (
            <p data-testid="sch-no-slots" style={{ color: "#64748b", fontSize: 13 }}>No open times in the next few weeks for that method. Try another method or contact Edwin's team.</p>
          )}
          {Object.keys(groupedByDay).sort().map((day) => (
            <div key={day} style={{ background: "white", padding: 16, borderRadius: 4, border: "1px solid #e2e8f0" }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, color: NAVY }}>
                {new Date(day + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                {groupedByDay[day].map((s) => {
                  const key = `${s.starts_at}|${s.method}|${s.availability_id}`;
                  const on = selected && `${selected.starts_at}|${selected.method}|${selected.availability_id}` === key;
                  return (
                    <button key={key} data-testid={`sch-slot-${s.starts_at.slice(11,16)}-${s.method}`}
                             onClick={() => setSelected(s)}
                             style={{ padding: "8px 12px", borderRadius: 4,
                                       border: `2px solid ${on ? GOLD : "#e2e8f0"}`,
                                       background: on ? NAVY : "white",
                                       color: on ? CREAM : NAVY, fontSize: 13,
                                       cursor: "pointer", fontFamily: "'Space Mono', monospace" }}>
                      {new Date(s.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {selected && (
          <div style={{ marginTop: 20, background: "white", padding: 16, borderRadius: 4, border: `2px solid ${GOLD}` }}>
            <div style={eyebrowStyle}>Confirm your slot</div>
            <div style={{ marginTop: 8, fontSize: 14 }}>
              <b>{new Date(selected.starts_at).toLocaleString()}</b> · {selected.method}
            </div>
            <label style={{ display: "grid", gap: 4, marginTop: 12, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#475569" }}>
              <span>Anything Edwin should know before we meet? (optional)</span>
              <textarea data-testid="sch-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={1000}
                          style={{ padding: 10, border: "1px solid #cbd5e1", borderRadius: 4, fontFamily: "inherit", fontSize: 13 }} />
            </label>
            <button data-testid="sch-book-btn" onClick={book} disabled={booking}
                     style={{ marginTop: 12, padding: "10px 22px", background: NAVY, color: CREAM,
                              border: `2px solid ${GOLD}`, cursor: booking ? "wait" : "pointer",
                              fontSize: 13, letterSpacing: 1, textTransform: "uppercase",
                              fontFamily: "'Space Mono', monospace" }}>
              {booking ? "Booking…" : "Book this time"}
            </button>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }) { return <main data-testid="scheduling-page" style={{ minHeight: "100vh", background: CREAM, color: NAVY }}>{children}</main>; }
function Panel({ children }) { return <div style={{ maxWidth: 620, margin: "0 auto", padding: "80px 24px" }}><div style={{ borderTop: `4px solid ${GOLD}`, paddingTop: 24 }}>{children}</div></div>; }
const h1Style = { fontFamily: "Playfair Display, serif", fontSize: 30, margin: "8px 0 4px", lineHeight: 1.1 };
const eyebrowStyle = { fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: GOLD, textTransform: "uppercase" };
const linkStyle = { display: "inline-block", marginTop: 20, color: NAVY, textDecoration: "underline" };
